import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import ArticleDetailDialog from "@/components/ArticleDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Package, MoreVertical, Pencil, Trash2, Search, Eye, AlertTriangle, Boxes, Banknote, CalendarClock, TrendingUp, Truck } from "lucide-react";
import { toast } from "sonner";

export default function Articles() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [q, setQ] = useState("");
  const [dash, setDash] = useState(null);

  const load = () => api.get("/articles").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/stock/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const schema = [
    { name: "designation", label: "Désignation", required: true, col: 2 },
    { name: "reference", label: "Référence / SKU", placeholder: "Auto si vide" },
    { name: "categorie", label: "Catégorie", type: "select", options: settings.article_categories || [] },
    { name: "sous_categorie", label: "Sous-catégorie", type: "select", optionsFn: (form) => { const m = settings.article_subcategory_map || {}; return (form?.categorie && (m[form.categorie] || []).length) ? m[form.categorie] : (settings.article_subcategories || []); } },
    { name: "unite", label: "Unité", type: "select", options: settings.units || [] },
    { name: "prix_achat", label: "Prix d'achat (FCFA)", type: "number" },
    { name: "prix_vente", label: "Prix de vente (FCFA)", type: "number" },
    { name: "stock_min", label: "Stock minimum", type: "number" },
    { name: "stock_max", label: "Stock maximum", type: "number" },
    { name: "fournisseur_id", label: "Fournisseur", type: "select", options: suppliers.map((s) => ({ value: s.id, label: s.nom })) },
    { name: "gere_par_lot", label: "Gestion par lots", type: "select", options: [{ value: true, label: "Oui" }, { value: false, label: "Non" }] },
    { name: "image", label: "Image (URL)", col: 2 },
    { name: "description", label: "Description", type: "textarea" },
  ];

  const save = async (form) => {
    try {
      const fn = suppliers.find((s) => s.id === form.fournisseur_id);
      const payload = { ...form, fournisseur_name: fn?.nom || "", gere_par_lot: form.gere_par_lot === true || form.gere_par_lot === "true" };
      if (editing) await api.put(`/articles/${editing.id}`, payload);
      else await api.post("/articles", payload);
      toast.success(editing ? "Article mis à jour" : "Article créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet article ?")) return; await api.delete(`/articles/${id}`); toast.success("Supprimé"); load(); };

  const filtered = (items || []).filter((a) => !q || `${a.designation} ${a.reference} ${a.categorie}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Articles" subtitle="Catalogue, prix & niveaux de stock">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-article" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel article</Button>
      </PageHeader>

      {dash && dash.expiring_count > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="expiry-alerts">
          <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2"><CalendarClock className="h-4 w-4" />Alertes de péremption — {dash.expiring_count} lot(s) à surveiller</div>
          <div className="flex flex-wrap gap-2">
            {dash.expiring_lots.map((l, i) => (
              <span key={i} data-testid={`expiry-lot-${i}`} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${l.expired ? "bg-rose-100 border-rose-300 text-rose-700" : "bg-white border-amber-300 text-amber-800"}`}>
                <span className="font-mono font-semibold">{l.reference}</span>· {l.article_name} · {l.quantite} · {l.expired ? "EXPIRÉ" : "exp."} {l.date_expiration}
              </span>
            ))}
          </div>
        </div>
      )}

      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="stock-dashboard">
          <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 text-slate-400"><Boxes className="h-4 w-4" /><span className="text-[10px] uppercase font-mono">Articles</span></div><p className="font-head text-2xl font-bold mt-1">{dash.total_articles}</p></div>
          <div className={`rounded-xl border p-4 ${dash.low_stock_count > 0 ? "bg-rose-50 border-rose-200" : "bg-white"}`}><div className="flex items-center gap-2 text-slate-400"><AlertTriangle className={`h-4 w-4 ${dash.low_stock_count > 0 ? "text-rose-500" : ""}`} /><span className="text-[10px] uppercase font-mono">Stock bas</span></div><p className={`font-head text-2xl font-bold mt-1 ${dash.low_stock_count > 0 ? "text-rose-600" : ""}`}>{dash.low_stock_count}</p></div>
          <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 text-slate-400"><Banknote className="h-4 w-4" /><span className="text-[10px] uppercase font-mono">Valeur du stock</span></div><p className="font-head text-lg font-bold mt-1 text-brand-700">{fmtMoney(dash.stock_value)}</p></div>
          <div className="rounded-xl border bg-white p-4"><div className="flex items-center gap-2 text-slate-400"><CalendarClock className="h-4 w-4" /><span className="text-[10px] uppercase font-mono">Lots à péremption</span></div><p className={`font-head text-2xl font-bold mt-1 ${dash.expiring_count > 0 ? "text-amber-600" : ""}`}>{dash.expiring_count}</p></div>
        </div>
      )}

      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="purchases-panel">
          <div className="rounded-xl border bg-white p-4">
            <p className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" />Achats</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-rose-50 border border-rose-100 p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Dette fournisseurs</p><p className="font-head text-lg font-bold text-rose-600">{fmtMoney(dash.dette_fournisseurs)}</p></div>
              <div className="rounded-lg bg-slate-50 border p-3"><p className="text-[10px] uppercase font-mono text-slate-400">Frais sur achats</p><p className="font-head text-lg font-bold text-slate-800">{fmtMoney(dash.total_frais_achats)}</p></div>
            </div>
          </div>
          <div className="rounded-xl border bg-white p-4">
            <p className="text-[10px] uppercase font-mono tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Coût de revient par article</p>
            {(!dash.cout_revient || dash.cout_revient.length === 0) ? <p className="text-sm text-slate-400">Aucun achat enregistré.</p> : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto scrollbar-thin">
                {dash.cout_revient.map((c, i) => (
                  <div key={i} data-testid={`cout-revient-${i}`} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 truncate pr-2">{c.designation}</span>
                    <span className="font-mono font-semibold text-slate-800">{fmtMoney(c.cout_revient)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="pl-9" data-testid="article-filter" />
      </div>

      {items === null ? <Loader /> : filtered.length === 0 ? (
        <EmptyState icon={Package} title="Aucun article" subtitle="Créez vos articles pour gérer le stock." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Réf.</TableHead><TableHead>Désignation</TableHead><TableHead>Catégorie</TableHead>
              <TableHead>Prix vente</TableHead><TableHead>Stock</TableHead><TableHead>Fournisseur</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`article-row-${a.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(a.id)}>
                  <TableCell className="font-mono text-xs text-slate-500">{a.reference}</TableCell>
                  <TableCell className="font-semibold">{a.designation}</TableCell>
                  <TableCell className="text-slate-600">{a.categorie || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{fmtMoney(a.prix_vente)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 font-mono text-xs font-semibold ${a.low_stock ? "text-rose-600" : "text-slate-700"}`}>
                      {a.low_stock && <AlertTriangle className="h-3.5 w-3.5" />}{a.stock ?? 0} {a.unite}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{a.fournisseur_name || "—"}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`article-menu-${a.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailId(a.id)}><Eye className="h-4 w-4 mr-2" />Fiche & stock</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditing(a); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(a.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier l'article" : "Nouvel article"} schema={schema} initial={editing} onSubmit={save} />
      <ArticleDetailDialog articleId={detailId} onOpenChange={(o) => !o && setDetailId(null)} />
    </div>
  );
}
